import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Plus, MoreHorizontal, Trash2, Calendar, GripVertical, Star } from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useToast } from '@/components/ui/use-toast';

// --- Sortable Item Component ---
function SortableTaskItem({ task, onToggle, onDelete, onStar }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: task.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`group flex items-center gap-3 p-3 bg-white hover:bg-gray-50 rounded-xl border border-transparent hover:border-gray-100 transition-all ${task.completed ? 'opacity-60' : ''}`}
        >
            {/* Drag Handle */}
            <div {...attributes} {...listeners} className="cursor-grab text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                <GripVertical size={16} />
            </div>

            {/* Checkbox */}
            <button
                onClick={() => onToggle(task.id)}
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300
                    ${task.completed ? 'bg-blue-500 border-blue-500 scale-95' : 'border-gray-300 hover:border-blue-400'}`}
            >
                {task.completed && <Check size={12} className="text-white" strokeWidth={4} />}
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0 flex flex-col">
                <span className={`text-sm font-medium truncate transition-all ${task.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                    {task.text}
                </span>
                {task.date && <span className="text-[10px] text-gray-400 flex items-center gap-1"><Calendar size={10} /> {task.date}</span>}
            </div>

            {/* Actions */}
            <button
                onClick={() => onStar(task.id)}
                className={`transition-colors ${task.starred ? 'text-amber-400' : 'text-gray-200 hover:text-amber-400'}`}
            >
                <Star size={18} fill={task.starred ? "currentColor" : "none"} />
            </button>
            <button
                onClick={() => onDelete(task.id)}
                className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
            >
                <Trash2 size={16} />
            </button>
        </div>
    );
}

const TaskActionList = () => {
    const [tasks, setTasks] = useState([]);
    const [newTask, setNewTask] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const { toast } = useToast();

    // Sensors for DnD
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // Persistence
    useEffect(() => {
        const saved = localStorage.getItem('google_style_tasks');
        if (saved) setTasks(JSON.parse(saved));
        else {
            // Default Welcome Task
            setTasks([
                { id: '1', text: 'Llamar a prospectos nuevos', completed: false, starred: true, date: 'Hoy' },
                { id: '2', text: 'Enviar contratos pendientes', completed: false, starred: false }
            ]);
        }
    }, []);

    const updateTasks = (newTasks) => {
        setTasks(newTasks);
        localStorage.setItem('google_style_tasks', JSON.stringify(newTasks));
    };

    // Add Task
    const handleAddTask = (e) => {
        if (e?.key && e.key !== 'Enter') return;
        if (!newTask.trim()) return;

        const newTaskObj = {
            id: Date.now().toString(),
            text: newTask.trim(),
            completed: false,
            starred: false,
            date: null
        };

        updateTasks([...tasks, newTaskObj]);
        setNewTask('');
        setIsAdding(false);

        toast({
            title: "Tarea creada",
            description: newTaskObj.text,
            duration: 2000
        });
    };

    // Toggle Complete
    const toggleTask = (id) => {
        const updated = tasks.map(t =>
            t.id === id ? { ...t, completed: !t.completed } : t
        );
        updateTasks(updated);
    };

    // Delete Task
    const deleteTask = (id) => {
        updateTasks(tasks.filter(t => t.id !== id));
        toast({
            title: "Tarea eliminada",
            variant: "destructive",
            duration: 2000
        });
    };

    // Toggle Star
    const toggleStar = (id) => {
        const updated = tasks.map(t =>
            t.id === id ? { ...t, starred: !t.starred } : t
        );
        updateTasks(updated);
    };

    // Drag End Handler
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = tasks.findIndex(t => t.id === active.id);
        const newIndex = tasks.findIndex(t => t.id === over.id);

        updateTasks(arrayMove(tasks, oldIndex, newIndex));
    };

    // Computed Values
    const activeTasks = tasks.filter(t => !t.completed);
    const completedTasks = tasks.filter(t => t.completed);

    return (
        <Card className="border-0 shadow-none h-full flex flex-col bg-white overflow-hidden">
            {/* Header */}
            <CardHeader className="py-4 px-5 border-b border-gray-100 bg-white flex flex-row items-center justify-between sticky top-0 z-10">
                <div>
                    <CardTitle className="text-xl font-bold flex items-center gap-2 text-slate-800">
                        <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                        Mis Tareas
                    </CardTitle>
                </div>
                <Button
                    size="icon"
                    className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-md transition-all hover:scale-105 active:scale-95"
                    onClick={() => setIsAdding(true)}
                >
                    <Plus size={20} strokeWidth={2.5} style={{ stroke: '#374151', color: '#374151' }} />
                </Button>
            </CardHeader>

            <CardContent className="flex-1 overflow-y-auto p-2 bg-slate-50/50 custom-scrollbar">
                {/* Add Input */}
                {(isAdding || tasks.length === 0) && (
                    <div className="p-3 mb-2 bg-white rounded-xl shadow-sm border border-blue-100 animate-in fade-in slide-in-from-top-2">
                        <Input
                            autoFocus
                            placeholder="Nueva tarea..."
                            value={newTask}
                            onChange={(e) => setNewTask(e.target.value)}
                            onKeyDown={handleAddTask}
                            onBlur={() => !newTask && setIsAdding(false)}
                            className="border-0 shadow-none focus-visible:ring-0 px-0 text-sm font-medium"
                        />
                        <div className="flex justify-end pt-2">
                            <Button size="sm" onClick={handleAddTask} disabled={!newTask.trim()} className="h-7 text-xs font-bold">Añadir</Button>
                        </div>
                    </div>
                )}

                {/* Task List */}
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={tasks.map(t => t.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="space-y-1">
                            {/* Active Tasks First */}
                            {tasks.map(task => (
                                <SortableTaskItem
                                    key={task.id}
                                    task={task}
                                    onToggle={toggleTask}
                                    onDelete={deleteTask}
                                    onStar={toggleStar}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>

                {/* Empty State */}
                {tasks.length === 0 && !isAdding && (
                    <div className="text-center py-10 text-gray-400">
                        <p className="text-sm">Todo listo 🎉</p>
                        <Button variant="link" onClick={() => setIsAdding(true)} className="text-blue-500">Crear tarea</Button>
                    </div>
                )}
            </CardContent>

            {/* Footer Summary */}
            <div className="py-2 px-4 bg-white border-t border-gray-100 text-[10px] font-bold text-gray-400 flex justify-between uppercase tracking-wider">
                <span>{activeTasks.length} Pendientes</span>
                <span>{completedTasks.length} Completadas</span>
            </div>
        </Card>
    );
};

export default TaskActionList;
